// Adding React to allow us to run tests with this component in Platform
// eslint-disable-next-line no-unused-vars
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { EditorProvider } from '@tiptap/react';
import CharacterCount from '@tiptap/extension-character-count';
import TiptapImage from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import StarterKit from '@tiptap/starter-kit';
import styles from './styles.module.less';

/**
 *
 * @see {Documentation} https://tiptap.dev/
 */
const RichTextEditor = ({
  content = '<p></p>',
  onChange = content => content,
  onBlur = content => content,
  onCharacterCount = count => count,
  FixedToolbar = () => null,
  EditorFooter = () => null,
  onHandleDrop = null,
  // Custom image handler callback
  // Useful for uploading images via the consuming component's own API context
  // used in baseToolbar's media button
  imageLoader = null,
}) => {
  const [editor, setEditor] = useState(null);
  const initialContent = useRef(null);

  // Define extensions / config for the editor
  const extensions = [
    StarterKit,
    Underline,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    CharacterCount.configure(),
    TiptapImage.configure({
      // Allow images to render in tags.
      inline: true,
      // Adjust style of images
      HTMLAttributes: {
        class: styles.richTextImage,
      },
    }),
    Link.configure({
      // only autolink urls with a protocol
      validate: (href) => /^https?:\/\//.test(href),
      // prevent navigation when editing
      openOnClick: false,
    }),
  ];

  useEffect(() => {
    // Sync provided content with the editor
    if (editor && content && initialContent.current !== content) {
      initialContent.current = content;
      if (editor?.getHTML() === content) return;
      editor.commands.setContent(content, false, { preserveWhitespace: 'full' });
      onCharacterCount(editor.storage.characterCount.characters());
    }
  }, [editor, content, onCharacterCount]);

  return (
    <EditorProvider
      extensions={extensions}
      content={content}
      slotBefore={<FixedToolbar />}
      slotAfter={<EditorFooter />}
      onCreate={({ editor }) => {
        // Store a reference of the current editor
        setEditor(editor);
        onCharacterCount(editor.storage.characterCount.characters());
      }}
      onUpdate={({ editor }) => {
        onCharacterCount(editor.storage.characterCount.characters());
        onChange(editor.getHTML());
      }}
      onBlur={({ editor }) => {
        onCharacterCount(editor.storage.characterCount.characters());
        onBlur(editor.getHTML());
      }}
      // @see [https://prosemirror.net/docs/ref/#view.EditorProps]
      editorProps={{
        attributes: {
          // Remove default outline
          class: styles.ProsemirrorOverrides,
        },
        handleDrop: onHandleDrop,
        imageLoader,
      }}
    >
      {/** Floating and bubble menus would go here */}
    </EditorProvider>
  );
};

RichTextEditor.propTypes = {
  content: PropTypes.string,
  placeholder: PropTypes.string,
  characterLimit: PropTypes.number,
  onCharacterCount: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  FixedToolbar: PropTypes.func,
  EditorFooter: PropTypes.func,
  onHandleDrop: PropTypes.func,
  // expects to receive a callback `onLoad` that will be called with the image URL
  imageLoader: PropTypes.func,
};

export default RichTextEditor;
